import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { MasterItem, SearchWallpaper, buildPdpUrl } from '../../models/wallpaper.model';
import { SPACES, CATEGORIES, spaceIdFromToken, spaceSlugById } from '../../models/constants';
import { DataLayerService } from '../../services/datalayer.service';

@Component({
  selector: 'app-space-listing',
  templateUrl: './space-listing.component.html'
})
export class SpaceListingComponent implements OnInit, OnDestroy {
  // Space data
  space: any = null;
  spaceSlug = '';
  spaceId: number | null = null;
  bannerImage = '';
  bannerImages: string[] = [];
  bannerIndex = 0;
  private bannerTimer: any;

  // Wallpaper data
  wallpapers: SearchWallpaper[] = [];
  isLoading = false;
  loadError = false;
  totalRecords = 0;

  // All spaces for sidebar
  allSpaces: any[] = [];

  // Pagination
  currentPage = 1;
  readonly pageSize = 12;

  // Sort
  sortBy = 'Recommended';
  sortOptions = ['Recommended', 'Popular', 'Newest', 'Price: Low \u2192 High', 'Price: High \u2192 Low'];
  showSortDropdown = false;

  // Description
  showFullDescription = false;

  // Filter drawer
  showFiltersMobile = false;
  private expandedSections = new Set<string>(['m-category', 'm-space', 'm-type', 'm-quality', 'm-color', 'm-vastu']);
  selectedSpaceIds = new Set<number>();
  selectedCategoryIds = new Set<number>();
  selectedTypes = new Set<string>();
  selectedColorIds = new Set<number>();
  selectedThemeIds = new Set<number>();
  selectedQualities = new Set<string>();
  vastu = false;
  masters: any = {};

  // Wishlist
  wishlistIds = new Set<string>();

  private sub = new Subscription();
  private pageEnterTime = 0;
  private scrollMilestones = new Set<number>();
  private userActionCount = 0;
  private hesitationTimer: any;
  private exitIntentFired = false;

  // Static space banner images (same as home component)
  private readonly spaceImages: Record<string, string> = {
    'living-room': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-1733_0.jpg?generation=1768288053960446&alt=media',
    'living': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-1733_0.jpg?generation=1768288053960446&alt=media',
    'lounge': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-1733_0.jpg?generation=1768288053960446&alt=media',
    'bedroom': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-2353_0.jpg?generation=1771830549850949&alt=media',
    'bed-room': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-2353_0.jpg?generation=1771830549850949&alt=media',
    'master-bedroom': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-2353_0.jpg?generation=1771830549850949&alt=media',
    'bathroom': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-P-2343_0.jpg?generation=1771481705307422&alt=media',
    'bath': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-P-2343_0.jpg?generation=1771481705307422&alt=media',
    'washroom': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-P-2343_0.jpg?generation=1771481705307422&alt=media',
    'dining-room': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2374_0.jpg?generation=1772000120285992&alt=media',
    'dining': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2374_0.jpg?generation=1772000120285992&alt=media',
    'kids-room': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper-mockup-thumbnail%2FMAWP-C-2105_space1_MKP-2025-050.png?generation=1765894596058132&alt=media',
    'kids': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper-mockup-thumbnail%2FMAWP-C-2105_space1_MKP-2025-050.png?generation=1765894596058132&alt=media',
    'children': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper-mockup-thumbnail%2FMAWP-C-2105_space1_MKP-2025-050.png?generation=1765894596058132&alt=media',
    'pooja-room': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-2003_0.jpg?generation=1772005357000531&alt=media',
    'pooja': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-2003_0.jpg?generation=1772005357000531&alt=media',
    'puja': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Flist_thumbnail_MAWP-C-2003_0.jpg?generation=1772005357000531&alt=media',
    'restaurant': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2357_0.jpg?generation=1771912835184045&alt=media',
    'restaurants': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2357_0.jpg?generation=1771912835184045&alt=media',
    'cafe': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2357_0.jpg?generation=1771912835184045&alt=media',
    'restaurants-cafes-pubs': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2357_0.jpg?generation=1771912835184045&alt=media',
    'hotels-resorts': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2342_0.jpg?generation=1771824594826529&alt=media',
    'hotel': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2342_0.jpg?generation=1771824594826529&alt=media',
    'resort': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2342_0.jpg?generation=1771824594826529&alt=media',
    'spa': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2327_0.jpg?generation=1771819565872275&alt=media',
    'salon': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2327_0.jpg?generation=1771819565872275&alt=media',
    'salons-spas-wellness': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2327_0.jpg?generation=1771819565872275&alt=media',
    'office': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2312_0.jpg?generation=1771817771664841&alt=media',
    'offices': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2312_0.jpg?generation=1771817771664841&alt=media',
    'offices-corporate': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2312_0.jpg?generation=1771817771664841&alt=media',
    'corporate': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2312_0.jpg?generation=1771817771664841&alt=media',
    'home-offices-study': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2312_0.jpg?generation=1771817771664841&alt=media',
    'study': 'https://storage.googleapis.com/download/storage/v1/b/static-mahattaart-online/o/wallpaper%2Fspace2_MAWP-C-2312_0.jpg?generation=1771817771664841&alt=media',
    'kitchen': 'https://storage.googleapis.com/mahattaart-wallpaper/Kitchen.png',
  };

  constructor(
    public state: AppStateService,
    public router: Router,
    private route: ActivatedRoute,
    private wallpaperService: WallpaperService,
    private location: Location,
    private dataLayer: DataLayerService,
  ) {}

  ngOnInit() {
    this.loadMasters();
    this.wallpaperService.getCategories().subscribe({
      next: (res: any) => {
        const list = res?.data ?? res ?? [];
        this.categoriesList = list.map((c: any) => {
          const constMatch = CATEGORIES.find(cat => cat.id === c.id);
          return { id: c.id, name: c.name, display_name: c.display_name || (constMatch ? constMatch.display_name : c.name), slug: c.slug, count: c.count };
        });
      }
    });

this.sub.add(
  combineLatest([
    this.route.paramMap,
    this.route.queryParamMap
  ]).subscribe(([params, qp]) => {

    this.spaceSlug = params.get('slug') || '';

    // Accepts a readable slug (?space=bedroom) or a legacy numeric id (?space=9)
    // so links already in the wild keep working.
    const spToken = qp.get('space');
    this.spaceId = spToken ? spaceIdFromToken(spToken) : null;
    const page = qp.get('page');
    this.currentPage = page ? +page : 1;

    this.loadSpaceData();
    this.loadWallpapers();
  })
);

    this.sub.add(this.state.wishlistIds$.subscribe(ids => this.wishlistIds = ids));

    // B8/B9/B12 init
    this.pageEnterTime = Date.now();
    this.scrollMilestones = new Set();
    this.userActionCount = 0;
    this.exitIntentFired = false;
    this.startHesitationTimer();

    this.bannerTimer = setInterval(() => {
      if (this.bannerImages.length > 1) {
        this.bannerIndex = (this.bannerIndex + 1) % this.bannerImages.length;
      }
    }, 5000);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    if (this.bannerTimer) clearInterval(this.bannerTimer);
    clearTimeout(this.hesitationTimer);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);
    [25, 50, 75, 100].forEach(m => {
      if (pct >= m && !this.scrollMilestones.has(m)) {
        this.scrollMilestones.add(m);
        this.dataLayer.pushPlpScroll({ scrollPercent: m, timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000), filtersActive: this.buildActiveFilters() });
      }
    });
    this.userActionCount++;
  }

  private startHesitationTimer(): void {
    clearTimeout(this.hesitationTimer);
    this.hesitationTimer = setTimeout(() => {
      if (this.userActionCount < 3) {
        this.dataLayer.pushUserHesitation({ timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000), actionsTaken: this.userActionCount, filtersActive: this.buildActiveFilters() });
      }
    }, 90000);
  }

  @HostListener('document:mouseleave', ['$event'])
  onMouseLeave(e: MouseEvent): void {
    if (e.clientY <= 0 && !this.exitIntentFired) {
      this.exitIntentFired = true;
      this.dataLayer.pushExitIntent({ timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000), filtersActive: this.buildActiveFilters(), itemsViewed: this.wallpapers.length });
    }
  }

  goBack() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    } else {
      this.router.navigate(['/wallpaper-home']);
    }
  }

  categoriesList: MasterItem[] = [];
  get masterCategories(): MasterItem[] { return this.categoriesList.length > 0 ? this.categoriesList : (this.masters?.['category'] || []); }
  private readonly fallbackColors: MasterItem[] = [
    { id: 1, name: 'Red', display_name: 'Red', slug: 'red', hex: '#DC2626' },
    { id: 2, name: 'Blue', display_name: 'Blue', slug: 'blue', hex: '#2563EB' },
    { id: 3, name: 'Green', display_name: 'Green', slug: 'green', hex: '#16A34A' },
    { id: 4, name: 'Yellow', display_name: 'Yellow', slug: 'yellow', hex: '#EAB308' },
    { id: 5, name: 'Orange', display_name: 'Orange', slug: 'orange', hex: '#EA580C' },
    { id: 7, name: 'Purple', display_name: 'Purple', slug: 'purple', hex: '#9333EA' },
    { id: 8, name: 'Brown', display_name: 'Brown', slug: 'brown', hex: '#92400E' },
    { id: 9, name: 'Black', display_name: 'Black', slug: 'black', hex: '#1F2937' },
    { id: 10, name: 'White', display_name: 'White', slug: 'white', hex: '#F9FAFB' },
    { id: 13, name: 'Teal', display_name: 'Teal', slug: 'teal', hex: '#0D9488' },
  ];
  get masterColors(): MasterItem[] {
    const apiColors = this.masters?.color || this.masters?.['color'] || [];
    return apiColors.length > 0 ? apiColors : this.fallbackColors;
  }
  get masterThemes(): MasterItem[] { return this.masters?.['wallpaper-theme'] || []; }

  private loadMasters() {
    this.wallpaperService.getMasters().subscribe({
      next: (res: any) => { this.masters = res?.data ?? res ?? {}; }
    });
  }

  private loadSpaceData() {
    // Spaces are hardcoded — no API call needed
    this.allSpaces = SPACES
      .filter(s => !s.display_name.toLowerCase().includes('retail'))
      .map(s => ({
        id: s.id,
        name: s.display_name,
        display_name: s.display_name,
        slug: s.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        image: s.image
      }));
    this.space = this.allSpaces.find(s => String(s.id) === String(this.spaceId));
    if (!this.space) {
      this.space = this.allSpaces.find(s => s.slug === this.spaceSlug);
    }
    this.bannerImage = this.getBannerImage();
    if (this.space) {
      this.selectedSpaceIds = new Set([this.space.id]);
      this.state.updateContext({
        selected_space: this.space.display_name || this.space.name,
        selected_space_id: this.space.id,
        selected_category: null,
        selected_category_id: null,
        entry_point: 'space'
      });
    }
    if (!this.spaceId && this.space) {
      this.spaceId = this.space.id;
    }
  }

  private getBannerImage(): string {
    if (this.space?.image) return this.space.image;
    const slug = (this.space?.slug || this.spaceSlug || '').toLowerCase();
    if (this.spaceImages[slug]) return this.spaceImages[slug];
    // Normalize name to slug format for matching
    const nameKey = (this.space?.display_name || this.space?.category_name || this.space?.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (this.spaceImages[nameKey]) return this.spaceImages[nameKey];
    // Partial match
    const keys = Object.keys(this.spaceImages);
    const partial = keys.find(k => slug.includes(k) || k.includes(slug) || nameKey.includes(k) || k.includes(nameKey));
    return partial ? this.spaceImages[partial] : this.spaceImages['living-room'];
  }

  get spaceName(): string {
    return this.space?.display_name || this.space?.category_name || this.space?.name || this.spaceSlug || 'Spaces';
  }

  get spaceDescription(): string {
    return this.space?.description || '';
  }

  navigateToSpace(sp: any) {
    const slug = sp.slug || String(sp.id);
    this.router.navigate(['/wallpaper/space', slug], {
      queryParams: { space: spaceSlugById(sp.id) || sp.id }
    });
  }

  getSpaceThumb(sp: any): string {
    if (sp.image || sp.image_url || sp.thumbnail) return sp.image || sp.image_url || sp.thumbnail;
    const slug = (sp.slug || '').toLowerCase();
    if (this.spaceImages[slug]) return this.spaceImages[slug];
    const nameKey = (sp.display_name || sp.category_name || sp.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (this.spaceImages[nameKey]) return this.spaceImages[nameKey];
    const keys = Object.keys(this.spaceImages);
    const partial = keys.find(k => slug.includes(k) || k.includes(slug) || nameKey.includes(k) || k.includes(nameKey));
    return partial ? this.spaceImages[partial] : '';
  }

  getWallpaperImage(wp: any): string {
    // When a single space is selected, show the matching space mockup
    // Use == (loose) because Solr may return string IDs while spaceId is number
    if (this.spaceId) {
      if (wp.thumbnail_space1_id == this.spaceId && wp.space1) return wp.space1;
      if (wp.thumbnail_space2_id == this.spaceId && wp.space2) return wp.space2;
      if (wp.thumbnail_space3_id == this.spaceId && wp.space3) return wp.space3;
    }
    // Fallback: show flat design image when no space mockup matches
    return wp.list_thumbnail || '';
  }

  private loadStartTime = 0;

  loadWallpapers() {
    this.isLoading = true;
    this.loadError = false;
    this.loadStartTime = performance.now();

    const { sortby, sortdir } = this.getSortParams();
    const space = this.selectedSpaceIds.size > 0
      ? [...this.selectedSpaceIds].join(',')
      : (this.spaceId ? String(this.spaceId) : undefined);
    const category = this.selectedCategoryIds.size > 0 ? [...this.selectedCategoryIds].join(',') : undefined;
    const type = this.selectedTypes.size > 0 ? [...this.selectedTypes].join(',') : undefined;
    const theme = this.selectedThemeIds.size > 0 ? [...this.selectedThemeIds].join(',') : undefined;
    const colorNames = [...this.selectedColorIds].map(id => this.masterColors.find(c => c.id === id)?.name).filter(Boolean);
    const color = colorNames.length > 0 ? colorNames.join(',') : undefined;
    const quality = this.selectedQualities.size > 0 ? [...this.selectedQualities].join(',') : undefined;

    this.wallpaperService.searchWallpapers({
      space, category, type, theme, color, quality,
      vastu: this.vastu ? 'yes' : undefined,
      sortby, sortdir, page: this.currentPage, images: this.pageSize,
    }).subscribe({
      next: (res) => {
        this.wallpapers = (res.results || []).map(wp => ({ ...wp, display_image: this.getWallpaperImage(wp) }));
        this.totalRecords = res.total ?? 0;
        this.isLoading = false;
        this.dataLayer.setPageTitle(this.spaceName + ' Wallpapers');
        this.pushViewItemList();
        if (this.bannerImages.length === 0 && this.wallpapers.length > 0) {
          const imgs: string[] = [];
          for (const wp of this.wallpapers) {
            if (wp.space1) imgs.push(wp.space1);
            else if (wp.list_thumbnail) imgs.push(wp.list_thumbnail);
            if (imgs.length >= 5) break;
          }
          if (imgs.length > 0) this.bannerImages = imgs;
        }
      },
      error: () => { this.loadError = true; this.isLoading = false; }
    });
  }

  private pushViewItemList(): void {
    const filters: string[] = [];
    if (this.spaceId) filters.push('space:' + this.spaceName);
    this.selectedCategoryIds.forEach(id => filters.push('category:' + id));
    this.selectedColorIds.forEach(id => {
      const c = this.masterColors.find(x => x.id === id);
      filters.push('color:' + (c?.name || id));
    });
    this.selectedThemeIds.forEach(id => filters.push('theme:' + id));
    this.selectedTypes.forEach(t => filters.push('type:' + t));
    this.selectedQualities.forEach(q => filters.push('quality:' + q));
    if (this.vastu) filters.push('vastu:yes');

    this.dataLayer.pushViewItemList({
      listName: this.spaceName + ' Wallpapers',
      listId: 'space_' + (this.spaceSlug || ''),
      items: this.wallpapers,
      filters,
      sortType: this.sortBy,
      totalResults: this.totalRecords,
      categoryName: this.spaceName,
      space: this.spaceName,
      timeToLoad: Math.round((performance.now() - this.loadStartTime) / 10) / 100,
    });
  }

  private getSortParams(): { sortby: string; sortdir: string } {
    if (this.sortBy === 'Price: Low \u2192 High') return { sortby: 'price', sortdir: 'asc' };
    if (this.sortBy === 'Price: High \u2192 Low') return { sortby: 'price', sortdir: 'desc' };
    if (this.sortBy === 'Popular') return { sortby: 'updated_at', sortdir: 'desc' };
    if (this.sortBy === 'Newest') return { sortby: 'updated_at', sortdir: 'desc' };
    return { sortby: 'recommended', sortdir: 'desc' };
  }

  onSortChange(opt: string) {
    const prev = this.sortBy;
    this.sortBy = opt; this.showSortDropdown = false;
    this.dataLayer.pushSortChange({ sortType: opt, previousSort: prev, totalResults: this.totalRecords });
    this.currentPage = 1; this.loadWallpapers();
  }

  // Toggle helpers
  private toggleIdSet(set: Set<number>, id: number): Set<number> { const s = new Set(set); s.has(id) ? s.delete(id) : s.add(id); return s; }
  private toggleStringSet(set: Set<string>, val: string): Set<string> { const s = new Set(set); s.has(val) ? s.delete(val) : s.add(val); return s; }

  toggleSpace(id: number) { const was = this.selectedSpaceIds.has(id); const n = this.allSpaces.find(s => s.id === id)?.display_name || String(id); this.selectedSpaceIds = this.toggleIdSet(this.selectedSpaceIds, id); this.pushFilterEvt('space', n, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleCategory(id: number) { const was = this.selectedCategoryIds.has(id); this.selectedCategoryIds = this.toggleIdSet(this.selectedCategoryIds, id); this.pushFilterEvt('category', String(id), was); this.currentPage = 1; this.loadWallpapers(); }
  toggleType(type: string) { const was = this.selectedTypes.has(type); this.selectedTypes = this.toggleStringSet(this.selectedTypes, type); this.pushFilterEvt('type', type, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleColor(id: number) { const was = this.selectedColorIds.has(id); const n = this.masterColors.find(c => c.id === id)?.name || String(id); this.selectedColorIds = this.toggleIdSet(this.selectedColorIds, id); this.pushFilterEvt('color', n, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleTheme(id: number) { const was = this.selectedThemeIds.has(id); this.selectedThemeIds = this.toggleIdSet(this.selectedThemeIds, id); this.pushFilterEvt('theme', String(id), was); this.currentPage = 1; this.loadWallpapers(); }
  toggleQuality(q: string) { const was = this.selectedQualities.has(q); this.selectedQualities = this.toggleStringSet(this.selectedQualities, q); this.pushFilterEvt('quality', q, was); this.currentPage = 1; this.loadWallpapers(); }
  toggleVastu() { this.vastu = !this.vastu; this.pushFilterEvt('vastu', 'yes', !this.vastu); this.currentPage = 1; this.loadWallpapers(); }

  private get totalActiveFilterCount(): number {
    return this.selectedCategoryIds.size + this.selectedSpaceIds.size + this.selectedColorIds.size
      + this.selectedThemeIds.size + this.selectedTypes.size + this.selectedQualities.size + (this.vastu ? 1 : 0);
  }
  private buildActiveFilters(): string[] {
    const f: string[] = [];
    this.selectedSpaceIds.forEach(id => f.push('space:' + (this.allSpaces.find(s => s.id === id)?.display_name || id)));
    this.selectedCategoryIds.forEach(id => f.push('category:' + id));
    this.selectedColorIds.forEach(id => f.push('color:' + (this.masterColors.find(c => c.id === id)?.name || id)));
    this.selectedThemeIds.forEach(id => f.push('theme:' + id));
    this.selectedTypes.forEach(t => f.push('type:' + t));
    this.selectedQualities.forEach(q => f.push('quality:' + q));
    if (this.vastu) f.push('vastu:yes');
    return f;
  }
  private getActiveFilterValues(type: string): string {
    switch (type) {
      case 'space': return [...this.selectedSpaceIds].map(id => this.allSpaces.find(s => s.id === id)?.display_name || id).join(', ');
      case 'category': return [...this.selectedCategoryIds].join(', ');
      case 'color': return [...this.selectedColorIds].map(id => this.masterColors.find(c => c.id === id)?.name || id).join(', ');
      case 'theme': return [...this.selectedThemeIds].join(', ');
      case 'type': return [...this.selectedTypes].join(', ');
      case 'quality': return [...this.selectedQualities].join(', ');
      case 'vastu': return this.vastu ? 'yes' : '';
      default: return '';
    }
  }
  private pushFilterEvt(type: string, value: string, wasActive: boolean): void {
    const allValues = this.getActiveFilterValues(type);
    if (wasActive) {
      this.dataLayer.pushFilterRemove({ filterType: type, filterValue: allValues || value, filtersActive: this.buildActiveFilters(), totalResults: this.totalRecords });
    } else {
      this.dataLayer.pushFilterApply({ filterType: type, filterValue: allValues || value, totalActiveFilters: this.totalActiveFilterCount, totalResults: this.totalRecords });
    }
  }

  toggleSection(key: string) {
    if (this.expandedSections.has(key)) this.expandedSections.delete(key);
    else this.expandedSections.add(key);
  }
  isSectionExpanded(key: string): boolean { return this.expandedSections.has(key); }

  clearFilters() {
    const cleared = this.buildActiveFilters();
    this.selectedTypes = new Set(); this.selectedCategoryIds = new Set(); this.selectedColorIds = new Set();
    this.selectedThemeIds = new Set(); this.selectedQualities = new Set(); this.vastu = false;
    this.selectedSpaceIds = this.space ? new Set([this.space.id]) : new Set();
    this.sortBy = 'Recommended'; this.currentPage = 1;
    if (cleared.length > 0) this.dataLayer.pushFilterClearAll({ filtersCleared: cleared, totalResultsAfter: this.totalRecords });
    this.loadWallpapers();
  }

  get hasActiveFilters(): boolean {
    return this.selectedTypes.size > 0 || this.selectedCategoryIds.size > 0 ||
      this.selectedColorIds.size > 0 || this.selectedThemeIds.size > 0 ||
      this.selectedQualities.size > 0 || this.vastu ||
      (this.selectedSpaceIds.size > 1 || (this.selectedSpaceIds.size === 1 && !this.space));
  }

  get activeFilterChips(): { label: string; type: string; value: string; id?: number }[] {
    const chips: { label: string; type: string; value: string; id?: number }[] = [];
    this.selectedSpaceIds.forEach(id => {
      if (this.space && id === this.space.id) return;
      const sp = this.allSpaces.find(s => s.id === id);
      chips.push({ label: sp?.display_name || sp?.name || 'Space', type: 'space', value: String(id), id });
    });
    this.selectedCategoryIds.forEach(id => {
      const cat = this.masterCategories.find(c => c.id === id);
      chips.push({ label: cat?.display_name || cat?.name || 'Category', type: 'category', value: String(id), id });
    });
    this.selectedTypes.forEach(t => chips.push({ label: t, type: 'type', value: t }));
    this.selectedQualities.forEach(q => chips.push({ label: q, type: 'quality', value: q }));
    this.selectedColorIds.forEach(id => {
      const c = this.masterColors.find(x => x.id === id);
      chips.push({ label: c?.name || 'Color', type: 'color', value: String(id), id });
    });
    this.selectedThemeIds.forEach(id => {
      const t = this.masterThemes.find(x => x.id === id);
      chips.push({ label: t?.name || 'Theme', type: 'theme', value: String(id), id });
    });
    if (this.vastu) chips.push({ label: 'Vastu Friendly', type: 'vastu', value: 'yes' });
    return chips;
  }

  removeChip(chip: { type: string; value: string; id?: number }) {
    switch (chip.type) {
      case 'space': this.selectedSpaceIds = this.toggleIdSet(this.selectedSpaceIds, chip.id || +chip.value); break;
      case 'category': this.selectedCategoryIds = this.toggleIdSet(this.selectedCategoryIds, chip.id || +chip.value); break;
      case 'color': this.selectedColorIds = this.toggleIdSet(this.selectedColorIds, chip.id || +chip.value); break;
      case 'theme': this.selectedThemeIds = this.toggleIdSet(this.selectedThemeIds, chip.id || +chip.value); break;
      case 'type': this.selectedTypes = this.toggleStringSet(this.selectedTypes, chip.value); break;
      case 'quality': this.selectedQualities = this.toggleStringSet(this.selectedQualities, chip.value); break;
      case 'vastu': this.vastu = false; break;
    }
    this.currentPage = 1; this.loadWallpapers();
  }

  // Pagination
  get totalPages(): number { return Math.ceil(this.totalRecords / this.pageSize); }
  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: number[] = [1];
    if (this.currentPage > 3) pages.push(-1);
    for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(total - 1, this.currentPage + 1); i++) pages.push(i);
    if (this.currentPage < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }
  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.currentPage) return;
    const action = p === this.currentPage + 1 ? 'next' : p === this.currentPage - 1 ? 'prev' : 'page_number';
    this.currentPage = p;
    this.dataLayer.pushPaginationClick({ action, pageNumber: p, totalResults: this.totalRecords, itemsLoaded: p * this.pageSize });
    this.userActionCount++;
    this.router.navigate([], { relativeTo: this.route, queryParams: { page: p > 1 ? p : null }, queryParamsHandling: 'merge', replaceUrl: false });
    this.loadWallpapers();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Navigation
  navigateToPDP(wp: SearchWallpaper) {
    const idx = this.wallpapers.indexOf(wp) + 1;
    this.dataLayer.pushSelectItem({
      listName: this.spaceName + ' Wallpapers',
      listId: 'space_' + (this.spaceSlug || ''),
      listType: 'space',
      source: 'plp',
      item: wp, index: idx,
      categoryFallback: this.spaceName,
    });
    this.router.navigateByUrl(buildPdpUrl(wp));
  }
  navigateToSimilar(wp: SearchWallpaper, e: Event) {
    e.stopPropagation();
    const width = 1100, height = 700;
    const left = (screen.width - width) / 2, top = (screen.height - height) / 2;
    const noise = new Set(['wallpaper', 'pattern', 'design', 'digital', 'print', 'the', 'and', 'with', 'for', 'of', 'in', 'on', 'a', 'an']);
    const keywords = (wp.name || '').split(/[\s\-_]+/).filter(w => w.length > 2 && !noise.has(w.toLowerCase())).slice(0, 5).join(' ');
    const url = `/similar/${wp.slug || wp.id}?name=${encodeURIComponent(wp.name)}&keywords=${encodeURIComponent(keywords)}&collection=${wp.collection || ''}&category=${wp.collection || ''}`;
    window.open(url, '_blank', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  }

  // Solr can return `quality` either as a string ("exclusive") or a single-element array (["exclusive"]).
  // Solr can return `quality` as a string ("exclusive") or a single-element array.
  private hasQuality(wp: any, quality: string): boolean {
    const q = wp?.quality;
    if (!q) return false;
    if (Array.isArray(q)) return q.some((v: any) => String(v).toLowerCase() === quality);
    return String(q).toLowerCase() === quality;
  }

  isExclusive(wp: any): boolean { return this.hasQuality(wp, 'exclusive'); }
  isPremium(wp: any): boolean { return this.hasQuality(wp, 'premium'); }

  // Wishlist
  isWishlisted(id: string): boolean { return this.wishlistIds.has(id); }
  toggleWishlistItem(wp: any, e: Event) {
    e.stopPropagation();
    const id = wp.id.toString();
    const isAdding = !this.wishlistIds.has(id);
    this.state.toggleWishlist(id, { id, name: wp.name, image: wp.list_thumbnail, price: wp.price, wallpaper_type: wp.wallpaper_type || '' });
    if (isAdding) {
      this.dataLayer.pushAddToWishlist({ item: wp, source: 'plp', categoryFallback: this.spaceName });
    }
    this.userActionCount++;
  }
}
